import { useMemo, type ReactElement } from 'react'
import { openIntercomArticle } from '@util'
import { pluralize, type EntityId } from '@terros/common'
import { Input, Stack } from '@mantine/core'
import {
  AREA_VISIBILITY_OPTIONS,
  CLOSER_INTERACTION_ITEMS,
  CLOSER_MODELS,
  CLOSER_STATUS_ITEMS,
  COUNTRY_CODE_ITEMS,
  DISPOSITION_MODE_ITEMS,
  DUPLICATE_ACCOUNTS_ITEMS,
  INTERCOM_ARTICLES,
  LOCKABLE_TEAM_FIELD_ITEMS,
  LOCKABLE_USER_FIELD_ITEMS,
  RECORDING_REMINDER_ITEMS,
  STRICT_SUBAREA_ITEMS,
  translateLabels,
  UNIT_SYSTEM_SELECT_OPTIONS,
  useHasEntitlement,
  usePreference,
  useTranslation,
} from '@front/model'
import { PreferenceDefaultCloserAvailability } from './timeslots'
import { PreferenceSystemStrategy } from './PreferenceSystemStrategy'
import { PreferenceSwitch } from './PreferenceSwitch'
import { PreferenceSelect } from './PreferenceSelect'
import { PreferenceNumberInput } from './PreferenceNumberInput'
import { PreferenceMultiSelect } from './PreferenceMultiSelect'
import { PreferenceMetersInput } from './PreferenceMetersInput'
import { PreferenceColumnsSelect } from './PreferenceColumnsSelect'

const BOOKING_PERCENTAGE_BASE_ITEMS = [1, 2, 3].map((value) => ({
  value: (value * 100).toString(),
  label: `${value} ${pluralize(value, 'appointment')} per timeslot`,
}))

export const PreferenceGroupContent = ({ targetId }: { targetId: EntityId }): ReactElement => {
  const { t } = useTranslation()

  const { data: restrictSetterCloseEvents } = usePreference('restrictSetterCloseEvents', targetId)
  const { data: closerInteraction } = usePreference('closerInteraction', targetId)
  const { data: closerModel } = usePreference('closerModel', targetId)
  const closerModelItems = useMemo(() => [...CLOSER_MODELS, { label: 'System assigns closer', value: 'system' }], [])
  const bookingPercentageItems = useMemo(() => translateLabels(BOOKING_PERCENTAGE_BASE_ITEMS, t), [t])
  const strictSubareaItems = useMemo(() => translateLabels(STRICT_SUBAREA_ITEMS, t), [t])

  const areaVisibilityOptions = useMemo(() => translateLabels(AREA_VISIBILITY_OPTIONS, t), [t])
  const unitSystemOptions = useMemo(() => translateLabels(UNIT_SYSTEM_SELECT_OPTIONS, t), [t])
  const lockableUserFieldItems = useMemo(() => translateLabels(LOCKABLE_USER_FIELD_ITEMS, t), [t])
  const lockableTeamFieldItems = useMemo(() => translateLabels(LOCKABLE_TEAM_FIELD_ITEMS, t), [t])
  const hasNewRecorder = useHasEntitlement('recording')

  return (
    <Stack>
      <PreferenceSelect data={COUNTRY_CODE_ITEMS} defaultValue='US' preference='country' targetId={targetId} />
      <PreferenceSwitch preference='blockCustomLocationDetailEdits' targetId={targetId} />
      <PreferenceNumberInput
        allowDecimal={false}
        min={0}
        preference='inactiveDaysBeforeArchive'
        suffix={t(' days')}
        targetId={targetId}
        placeholder='60 days'
      />
      <PreferenceSwitch preference='canBeInMultipleTeams' targetId={targetId} />
      <PreferenceNumberInput
        allowDecimal={false}
        min={-1}
        placeholder={t('Disabled')}
        preference='daysBetweenAskManager'
        suffix={t(' days')}
        targetId={targetId}
      />
      <PreferenceSwitch preference='enableVisibilityRestrictions' targetId={targetId} />
      <PreferenceMultiSelect data={lockableUserFieldItems} preference='lockedUserFields' targetId={targetId} />
      <PreferenceMultiSelect data={lockableTeamFieldItems} preference='lockedTeamFields' targetId={targetId} />
      <PreferenceNumberInput
        allowDecimal={false}
        min={0}
        placeholder='90 days'
        preference='daysBetweenInterview'
        suffix={t(' days')}
        targetId={targetId}
      />
      <PreferenceNumberInput
        allowDecimal={false}
        min={0}
        placeholder={t('7 days')}
        preference='daysBetweenShadow'
        suffix={t(' days')}
        targetId={targetId}
      />
      <PreferenceSelect data={CLOSER_INTERACTION_ITEMS} preference='closerInteraction' targetId={targetId} />
      {closerInteraction !== 'None' && (
        <>
          <PreferenceSelect
            data={CLOSER_STATUS_ITEMS}
            preference='closerStatus'
            targetId={targetId}
            description={
              <Input.Description>
                {t('The status that will be assumed for all users that do not have their closer status set.')}{' '}
                <button
                  className='text-blue-400'
                  type='button'
                  onClick={() => openIntercomArticle(INTERCOM_ARTICLES.closerStatusOverview)}
                >
                  {t('More Info')}
                </button>
              </Input.Description>
            }
          />
          <PreferenceSelect data={closerModelItems} preference='closerModel' targetId={targetId} />
          {closerModel === 'system' && <PreferenceSystemStrategy targetId={targetId} />}
          <PreferenceSwitch preference='restrictSetterCloseEvents' targetId={targetId} />
          {restrictSetterCloseEvents && (
            <PreferenceSelect
              data={bookingPercentageItems}
              defaultValue='100'
              preference='bookingPercent'
              targetId={targetId}
            />
          )}
          <PreferenceNumberInput
            allowDecimal={false}
            min={15}
            preference='meetingDuration'
            suffix={t(' minutes')}
            targetId={targetId}
          />
          <PreferenceNumberInput
            allowDecimal={false}
            min={0}
            preference='timeBetweenAppointments'
            suffix={t(' minutes')}
            targetId={targetId}
          />
          <PreferenceDefaultCloserAvailability targetId={targetId} />
          <PreferenceNumberInput
            allowDecimal={false}
            min={-1}
            placeholder={t('Disabled')}
            preference='updateAvailabilityTask'
            suffix={t(' days')}
            targetId={targetId}
          />
          <PreferenceNumberInput
            allowDecimal={false}
            min={-1}
            placeholder={t('60 minutes')}
            preference='maxMinutesSetterCanDeleteEvent'
            suffix={t(' minutes')}
            targetId={targetId}
          />
        </>
      )}

      <PreferenceNumberInput
        min={0}
        placeholder={t('90 sec')}
        preference='secondsBetweenPitches'
        suffix={t(' sec')}
        targetId={targetId}
      />
      <PreferenceMetersInput min={0} preference='metersBetweenPitchesToday' targetId={targetId} />
      <PreferenceMetersInput min={0} preference='maxMetersFromLocationToActivity' targetId={targetId} />
      <PreferenceMetersInput min={0} preference='maxMetersFromLocationToPitch' targetId={targetId} />
      <PreferenceNumberInput
        min={-1}
        placeholder='-1'
        preference='daysBetweenPitch'
        suffix={t(' days')}
        targetId={targetId}
      />

      <PreferenceMetersInput min={0} preference='maxMetersFromEventToDisposition' targetId={targetId} />
      <PreferenceMetersInput min={0} preference='conversationLocationUpdateThreshold' targetId={targetId} />
      <PreferenceSelect data={DISPOSITION_MODE_ITEMS} preference='dispositionMode' targetId={targetId} />
      <PreferenceSwitch preference='showCommercialProperties' targetId={targetId} />
      <PreferenceSwitch preference='showMultiUnitDwellings' targetId={targetId} />
      <PreferenceSwitch preference='phonePitch' targetId={targetId} />
      <PreferenceSwitch preference='limitByArea' targetId={targetId} />
      <PreferenceSelect data={strictSubareaItems} preference='strictSubareaAssignment' targetId={targetId} />
      <PreferenceSwitch preference='requireEvalToCanvass' targetId={targetId} />
      {!hasNewRecorder && <PreferenceSwitch preference='autoAssignEvals' targetId={targetId} />}
      <PreferenceNumberInput
        min={0}
        suffix={t(' days')}
        placeholder={t('Never')}
        preference='conversationExpiration'
        targetId={targetId}
      />
      <PreferenceSelect
        data={translateLabels(RECORDING_REMINDER_ITEMS, t)}
        placeholder={t('Never')}
        preference='recordingReminder'
        targetId={targetId}
      />
      <PreferenceSwitch preference='taskLockout' targetId={targetId} />
      <PreferenceSwitch preference='planLockout' targetId={targetId} />
      <PreferenceSelect data={areaVisibilityOptions} preference='areaVisibility' targetId={targetId} />
      <PreferenceSelect data={unitSystemOptions} preference='unitSystem' targetId={targetId} />
      <DuplicateAccountsSelect targetId={targetId} />
      <PreferenceSwitch preference='hideInactivePins' targetId={targetId} />
      <PreferenceColumnsSelect targetId={targetId} preference='leaderboardColumns' />
      <PreferenceColumnsSelect targetId={targetId} preference='defaultStatsColumns' />
    </Stack>
  )
}

const DuplicateAccountsSelect = ({ targetId }: { targetId: EntityId }): ReactElement => {
  return <PreferenceSelect data={DUPLICATE_ACCOUNTS_ITEMS} preference='allowDuplicateAccounts' targetId={targetId} />
}
